import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  Icon,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@nexus/ui";
import type {
  ChangelogChannel,
  ChangelogSections,
  ChangelogSignificance,
} from "@/features/changelog/api/changelog.api";
import {
  canApprove,
  canDiscard,
  canEdit,
  canRegenerate,
  canRevokeApproval,
  canSend,
  formatDeployedAt,
  getEntryState,
  getStage,
  type ChangelogSendable,
} from "@/features/changelog/lib/changelog-display";
import { FailurePanel } from "@/components/FailurePanel";
import { ChangelogDeliveryList } from "./shared/ChangelogDeliveryList";
import { ChangelogSectionList } from "./shared/ChangelogSectionList";
import { ChangelogStageIndicator } from "./shared/ChangelogStageIndicator";
import { ChangelogStatusBadge } from "./shared/ChangelogStatusBadge";
import {
  ChangelogEntryEditor,
  editorFormId,
  type ChangelogEditorSubmission,
} from "./ChangelogEntryEditor";

export interface ChangelogDetailTarget extends ChangelogSendable {
  headline: string | null;
  sections: ChangelogSections;
  /** A deploy's big-news-or-small-update label. Roundups do not carry one. */
  significance?: ChangelogSignificance;
  generationError: string | null;
  editedAt: string | null;
  editedByName: string | null;
  approvedByName: string | null;
}

/** What this admin may do, before the state machine has its say. */
export interface ChangelogDetailCapabilities {
  edit: boolean;
  approve: boolean;
  revokeApproval: boolean;
  send: boolean;
  regenerate: boolean;
  discard: boolean;
}

interface ChangelogDetailSheetProps {
  /** Decides the copy: a deploy is engineer-facing, a roundup is not. */
  kind: "deploy" | "roundup";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  isTitleMono?: boolean;
  /** Provenance line under the title — version, branch, who deployed or composed. */
  meta: ReactNode;
  target: ChangelogDetailTarget;
  /** Rendered under the notes: coverage for a roundup, nothing for a deploy. */
  extra?: ReactNode;
  channels: ChangelogChannel[];
  capabilities: ChangelogDetailCapabilities;
  isBusy: boolean;
  isEditing: boolean;
  /** Drives the footer's save button while editing. */
  isDirty: boolean;
  isSaving: boolean;
  saveError: string | null;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onDirtyChange: (isDirty: boolean) => void;
  onSave: (input: ChangelogEditorSubmission) => void;
  onRegenerate: () => void;
  onApprove: () => void;
  onRevokeApproval: () => void;
  onDiscard?: () => void;
  onOpenSend: () => void;
  onRetryChannel: (channel: ChangelogChannel, force: boolean) => void;
}

/**
 * The review surface for one unit of changelog, deploy or roundup.
 *
 * A list row exists to be scanned and a detail panel exists to be read and
 * acted on, so the two carry different status devices: the row a badge, this a
 * three-step indicator that also says what comes next. Actions collapse to the
 * single one the current state allows, in the footer, where the eye lands last.
 */
export function ChangelogDetailSheet({
  kind,
  isOpen,
  onOpenChange,
  title,
  isTitleMono = false,
  meta,
  target,
  extra,
  channels,
  capabilities,
  isBusy,
  isEditing,
  isDirty,
  isSaving,
  saveError,
  onStartEdit,
  onCancelEdit,
  onDirtyChange,
  onSave,
  onRegenerate,
  onApprove,
  onRevokeApproval,
  onDiscard,
  onOpenSend,
  onRetryChannel,
}: ChangelogDetailSheetProps) {
  const { t, i18n } = useTranslation();
  const state = getEntryState(target);
  const isRoundup = kind === "roundup";

  const showRegenerate = capabilities.regenerate && canRegenerate(target);
  const showEdit = capabilities.edit && canEdit(target);
  const showApprove = capabilities.approve && canApprove(target);
  const showRevoke = capabilities.revokeApproval && canRevokeApproval(target);
  const showSend = capabilities.send && canSend(target, channels);
  const showDiscard = Boolean(onDiscard) && capabilities.discard && canDiscard(target);
  const hasActions =
    showRegenerate || showEdit || showApprove || showRevoke || showSend || showDiscard;

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent aria-describedby={undefined}>
        <SheetHeader className="gap-0 pr-12">
          <SheetTitle className={cn(isTitleMono && "font-mono")}>{title}</SheetTitle>
          <div className="mt-1 text-[11px] text-muted-foreground">{meta}</div>
          <div className="mt-3">
            {target.status === "READY" ? (
              <ChangelogStageIndicator stage={getStage(target)} />
            ) : (
              <ChangelogStatusBadge state={state} isDetailed />
            )}
          </div>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-5">
          {state === "failed" ? (
            <FailurePanel
              message={
                target.generationError ??
                (isRoundup
                  ? t("admin.changelog.roundups.failed", "Could not merge the picked items.")
                  : t("admin.changelog.failed", "Could not generate notes for this release."))
              }
            />
          ) : null}

          {state === "stalled" ? (
            <div className="rounded-md border border-error-border bg-error-subtle px-4 py-3 text-xs text-error-subtle-foreground">
              {t(
                "admin.changelog.stalled",
                "This was queued for the writer but never came back — most likely the worker restarted mid-run. Regenerate to try again.",
              )}
            </div>
          ) : null}

          {state === "generating" || state === "pending" ? (
            <p className="m-0 flex items-center gap-2 text-xs text-muted-foreground">
              <Icon
                name="loader"
                size="xs"
                className="animate-spin motion-reduce:animate-none"
                aria-hidden
              />
              {isRoundup
                ? t("admin.changelog.roundups.generating", "Merging the picked items into one message…")
                : t("admin.changelog.generating", "Writing release notes from this release's commits…")}
            </p>
          ) : null}

          {isEditing ? (
            <ChangelogEntryEditor
              editorId={title}
              source={{
                title: isRoundup ? title : null,
                headline: target.headline,
                significance: target.significance ?? null,
                sections: target.sections,
              }}
              saveError={saveError}
              onSave={onSave}
              onDirtyChange={onDirtyChange}
            />
          ) : (
            <>
              {target.headline ? (
                <p className="m-0 max-w-[68ch] text-[15px] leading-relaxed text-foreground">
                  {target.headline}
                </p>
              ) : null}

              <div className="flex flex-col gap-3">
                <ChangelogSectionList sections={target.sections} />
              </div>

              {target.status === "READY" && target.itemCount === 0 ? (
                <p className="m-0 text-xs text-muted-foreground">
                  {isRoundup
                    ? t("admin.changelog.roundups.emptyNotes", "This roundup ended up with nothing in it.")
                    : t(
                        "admin.changelog.noUserFacingChanges",
                        "Nothing user-facing shipped in this release.",
                      )}
                </p>
              ) : null}

              {extra}

              {target.editedByName && target.editedAt ? (
                <p className="m-0 text-[11px] text-muted-foreground">
                  {t("admin.changelog.editedBy", "Edited by {{by}} on {{at}}", {
                    by: target.editedByName,
                    at: formatDeployedAt(target.editedAt, i18n.language),
                  })}
                </p>
              ) : null}

              {target.deliveries.length > 0 ? (
                <section className="flex flex-col gap-2 border-t border-border-subtle pt-4">
                  <h3 className="m-0 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {t("admin.changelog.detail.delivery", "Delivery")}
                  </h3>
                  <ChangelogDeliveryList
                    deliveries={target.deliveries}
                    canRetry={capabilities.send}
                    isBusy={isBusy}
                    onRetry={onRetryChannel}
                  />
                </section>
              ) : null}
            </>
          )}
        </SheetBody>

        {isEditing ? (
          <SheetFooter className="justify-between px-6">
            <span className="text-[11px] text-muted-foreground">
              {isDirty
                ? t("admin.changelog.editor.unsaved", "Unsaved changes")
                : t("admin.changelog.editor.noChanges", "Nothing changed yet")}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-sm text-xs"
                disabled={isSaving}
                onClick={onCancelEdit}
              >
                {t("admin.changelog.editor.cancel", "Cancel")}
              </Button>
              <Button
                type="submit"
                form={editorFormId(title)}
                size="sm"
                className="h-8 rounded-sm text-xs"
                disabled={isSaving || !isDirty}
              >
                {isSaving
                  ? t("admin.changelog.editor.saving", "Saving…")
                  : t("admin.changelog.editor.save", "Save changes")}
              </Button>
            </div>
          </SheetFooter>
        ) : hasActions ? (
          <SheetFooter className="justify-between px-6">
            <div className="flex items-center gap-2">
              {showDiscard ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 rounded-sm text-xs text-error"
                  disabled={isBusy}
                  onClick={onDiscard}
                >
                  {t("admin.changelog.roundups.discard", "Discard")}
                </Button>
              ) : null}
              {showRevoke ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 rounded-sm text-xs"
                  disabled={isBusy}
                  onClick={onRevokeApproval}
                >
                  {t("admin.changelog.revokeApproval", "Reopen for editing")}
                </Button>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              {showRegenerate ? (
                <Button
                  variant="ghost"
                  size="sm"
                  iconStart="refresh-cw"
                  className="h-8 rounded-sm text-xs"
                  disabled={isBusy}
                  onClick={onRegenerate}
                >
                  {t("admin.changelog.regenerate", "Regenerate")}
                </Button>
              ) : null}
              {showEdit ? (
                <Button
                  variant="outline"
                  size="sm"
                  iconStart="pencil"
                  className="h-8 rounded-sm text-xs"
                  disabled={isBusy}
                  onClick={onStartEdit}
                >
                  {t("admin.changelog.edit", "Edit")}
                </Button>
              ) : null}
              {showApprove ? (
                <Button
                  size="sm"
                  iconStart="check"
                  className="h-8 rounded-sm text-xs"
                  disabled={isBusy}
                  onClick={onApprove}
                >
                  {t("admin.changelog.approve", "Approve")}
                </Button>
              ) : null}
              {showSend ? (
                <Button size="sm" className="h-8 rounded-sm text-xs" disabled={isBusy} onClick={onOpenSend}>
                  {isRoundup
                    ? t("admin.changelog.roundups.sendOpen", "Send roundup…")
                    : t("admin.changelog.sendToSlack", "Post to Slack…")}
                </Button>
              ) : null}
            </div>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
